/**
 * The QUIPWA mark: a quipu reduced to its idea.
 *
 * A quipu is the Andean record made of cords: a main cord, pendant cords
 * hanging from it, and knots whose position and count hold the data. Like a
 * deck, it is memory you can read back.
 *
 * One geometry, three outputs:
 *   - <QuipuMark/> in the app (colours from the active theme);
 *   - public/favicon.svg and public/pwa-icon.svg, written from
 *     renderQuipuSvg() by `npm run icons`, which also renders the PNG icons;
 *   - quipu.test.ts fails if the files in public/ drift from this module.
 *
 * Erasable TypeScript only: scripts/build-icons.mjs imports it with Node's
 * --experimental-strip-types.
 */

/** On a 32-unit grid. */
export const QUIPU = {
  /** The main cord sags a little at the centre, so it reads as rope, not a rake. */
  cord: { from: 5, to: 27, y: 8, sag: 1.5, width: 2.4 },
  pendantWidth: 1.8,
  /** Small enough to read as a bulge on the cord, not a bead. */
  knotRadius: 1.45,
  pendants: [
    { x: 8.5, end: 22, knots: [13.5, 17.5] },
    { x: 13.5, end: 18.5, knots: [14.5] },
    { x: 18.5, end: 25.5, knots: [13.5, 17.5, 21.5] },
    { x: 23.5, end: 20.5, knots: [15.5] },
  ],
} as const

/** Vertical centre of the drawing, used to centre it in a square. */
export const QUIPU_CENTER_Y = 16.8

const round = (n: number) => Math.round(n * 100) / 100

/** Height of the main cord at `x` (a quadratic sag, deepest at the centre). */
export function cordY(x: number): number {
  const { from, to, y, sag } = QUIPU.cord
  const t = (x - from) / (to - from)
  return y + 4 * sag * t * (1 - t)
}

export interface QuipuShapes {
  cord: string
  pendants: string[]
  knots: Array<{ cx: number; cy: number }>
}

/** SVG path data and knot positions, in grid units. */
export function quipuShapes(): QuipuShapes {
  const { from, to, y, sag } = QUIPU.cord
  return {
    cord: `M${from} ${y}Q${(from + to) / 2} ${round(y + 2 * sag)} ${to} ${y}`,
    pendants: QUIPU.pendants.map((p) => `M${p.x} ${round(cordY(p.x))}V${p.end}`),
    knots: QUIPU.pendants.flatMap((p) => p.knots.map((cy) => ({ cx: p.x, cy }))),
  }
}

export interface QuipuSvgOptions {
  /** viewBox side. */
  box: number
  /** Grid units → viewBox units. */
  scale: number
  background: string
  /** Corner radius of the background; 0 for full-bleed (maskable) icons. */
  radius: number
  cord: string
  knot: string
  comment: string
}

/** A standalone SVG document of the mark. */
export function renderQuipuSvg(o: QuipuSvgOptions): string {
  const s = quipuShapes()
  const c = o.box / 2
  const rx = o.radius > 0 ? ` rx="${o.radius}"` : ''
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${o.box} ${o.box}" fill="none">`,
    `  <!-- ${o.comment} -->`,
    `  <rect width="${o.box}" height="${o.box}"${rx} fill="${o.background}"/>`,
    `  <g transform="translate(${c} ${c}) scale(${o.scale}) translate(-16 -${QUIPU_CENTER_Y})">`,
    `    <path d="${s.cord}" stroke="${o.cord}" stroke-width="${QUIPU.cord.width}" stroke-linecap="round"/>`,
    `    <path d="${s.pendants.join('')}" stroke="${o.cord}" stroke-width="${QUIPU.pendantWidth}" stroke-linecap="round"/>`,
    `    <g fill="${o.knot}">`,
    ...s.knots.map((k) => `      <circle cx="${k.cx}" cy="${k.cy}" r="${QUIPU.knotRadius}"/>`),
    `    </g>`,
    `  </g>`,
    `</svg>`,
    '',
  ].join('\n')
}

/** Brand colours of the icons: the default theme (Quipwa Dark). */
const INK = { background: '#0A0F0B', cord: '#E8E0D0', knot: '#1D9E75' }

/** Browser tab: rounded tile, so it reads on light and dark tab bars. */
export const FAVICON_SVG = renderQuipuSvg({
  box: 32, scale: 1, radius: 7, ...INK,
  comment: 'QUIPWA: a quipu. Generated by `npm run icons` from src/lib/brand/quipu.ts',
})

/**
 * App icon. Full-bleed: the OS applies its own mask (maskable), so the quipu
 * stays inside the central safe zone, a circle of radius 0.4 × size.
 */
export const PWA_ICON_SVG = renderQuipuSvg({
  box: 512, scale: 11, radius: 0, ...INK,
  comment: 'QUIPWA app icon (maskable). Generated by `npm run icons` from src/lib/brand/quipu.ts',
})
